import { estimatedPackages } from '@/components/po-estimated-package-facts-model'
import type { PurchaseOrderDetail } from './types'

type Shipment = NonNullable<PurchaseOrderDetail['shipments']>[number]
type HandlingUnit = Shipment['handling_units'][number]

const positive = (value: string | number | null | undefined) => Number(value ?? 0) > 0

export type FreightMode = 'parcel' | 'ltl'

export const FREIGHT_MODE_LABEL: Record<FreightMode, string> = { parcel: 'Small parcel', ltl: 'LTL' }

/** The mode a shipment books under: its own, else the PO's. */
export function shipmentFreightMode(
  shipment: Pick<Shipment, 'freight_mode'>,
  po: Pick<PurchaseOrderDetail, 'freight_mode'>,
): FreightMode | null {
  const mode = shipment.freight_mode ?? po.freight_mode
  return mode === 'parcel' || mode === 'ltl' ? mode : null
}

/** Statuses where the shipment has not been booked with a carrier yet. */
export const UNBOOKED_SHIPMENT_STATUSES = new Set(['pending', 'ready_to_book'])

/** A shipment we still have to book: booking inputs lead, carrier numbers come from the booking. */
export function isShipmentToBook(shipment: Pick<Shipment, 'status' | 'booked_by'>) {
  return shipment.booked_by === 'us' && UNBOOKED_SHIPMENT_STATUSES.has(shipment.status)
}

function unitWeight(unit: HandlingUnit) {
  return (unit.commodities ?? []).reduce((sum, c) => sum + Number(c.weight_lbs ?? 0), 0)
}

/** What the boxes / pallets still need, per unit, in plain words. */
export function handlingUnitsMissing(units: readonly HandlingUnit[]): string[] {
  if (units.length === 0) return ['box or pallet details']
  const missing: string[] = []
  units.forEach((unit, index) => {
    const name = `${unit.unit_type.toLowerCase()} ${index + 1}`
    if (!positive(unit.length_in) || !positive(unit.width_in) || !positive(unit.height_in))
      missing.push(`${name} size`)
    if (unitWeight(unit) <= 0) missing.push(`${name} weight`)
  })
  return missing
}

export function pickupAddressMissing(shipment: Shipment): string[] {
  const missing: string[] = []
  if (!shipment.ship_from_name) missing.push('pickup name')
  if (!shipment.ship_from_address1 || !shipment.ship_from_city || !shipment.ship_from_state)
    missing.push('pickup address')
  if (!shipment.ship_from_zip) missing.push('pickup ZIP')
  return missing
}

/**
 * What a shipment we book still needs before it can be booked, in plain words.
 * Mirrors the server's ready-to-book check; the Book endpoint re-checks.
 */
export function shipmentBookingMissing(
  shipment: Shipment,
  shipToPhone: string | null | undefined,
): string[] {
  const missing: string[] = []
  if (!positive(shipment.total_weight_lbs)) missing.push('total weight')
  missing.push(...pickupAddressMissing(shipment))
  if (!shipment.ship_from_phone) missing.push('pickup phone')
  if (!shipToPhone) missing.push('delivery phone')
  missing.push(...handlingUnitsMissing(shipment.handling_units ?? []))
  return missing
}

/** Handling units as the edit and create endpoints take them. */
export interface HandlingUnitInput {
  unit_type: string
  num_units: number
  stackable: boolean
  length_in: number | null
  width_in: number | null
  height_in: number | null
  commodities: Array<{ description: string; weight_lbs: number; pieces: number }>
}

/** The PO's estimated packages as handling units, when the PO carries an estimate. */
export function estimatedHandlingUnits(po: PurchaseOrderDetail): HandlingUnitInput[] {
  const estimate = estimatedPackages((po.estimated_package_facts ?? {}) as Record<string, unknown>)
  if (!estimate) return []
  // One entry per physical package, as booking reads them (book_shipping._estimated_booking_handling_units).
  return estimate.packages.map((pkg) => {
    const weight = Number(pkg.weight_lbs)
    const dim = (value: unknown) => (positive(value as number) ? Number(value) : null)
    return {
      unit_type: 'Carton',
      num_units: 1,
      stackable: false,
      length_in: dim(pkg.length_in),
      width_in: dim(pkg.width_in),
      height_in: dim(pkg.height_in),
      commodities:
        weight > 0 ? [{ description: `Restaurant equipment PO ${po.po_number}`, weight_lbs: weight, pieces: 1 }] : [],
    }
  })
}

/** The PO's most recent shipment with a pickup address, to carry forward on a new one. */
export function priorShipment(po: PurchaseOrderDetail, excludeId?: number): Shipment | undefined {
  const sorted = [...(po.shipments ?? [])]
    .filter((s) => s.id !== excludeId)
    .sort((a, b) => (b.updated_at ?? b.uuid).localeCompare(a.updated_at ?? a.uuid))
  return sorted.find((s) => s.ship_from_address1) ?? sorted[0]
}

/** Where a pickup address comes from when the shipment has none: a prior shipment, then the manufacturer. */
export function pickupDefaults(po: PurchaseOrderDetail, excludeId?: number) {
  const prior = priorShipment(po, excludeId)
  const m = po.manufacturer
  return {
    ship_from_name: prior?.ship_from_name || m?.name || '',
    ship_from_contact_name: prior?.ship_from_contact_name || '',
    ship_from_phone: prior?.ship_from_phone || m?.phone || '',
    ship_from_email: prior?.ship_from_email || '',
    ship_from_address1: prior?.ship_from_address1 || m?.address1 || '',
    ship_from_address2: prior?.ship_from_address2 || m?.address2 || '',
    ship_from_city: prior?.ship_from_city || m?.city || '',
    ship_from_state: prior?.ship_from_state || m?.state || '',
    ship_from_zip: prior?.ship_from_zip || m?.zip_code || '',
  }
}

/** One line per kind of package, identical ones counted together: "4 × carton · 15 × 13 × 63 in · 144.25 lb". */
export function groupedUnitSummaries(units: readonly HandlingUnit[]): string[] {
  const counts = new Map<string, { type: string; count: number }>()
  for (const unit of units) {
    const dims = [unit.length_in, unit.width_in, unit.height_in]
    const size = dims.every((d) => positive(d)) ? `${dims.map((d) => Number(d)).join(' × ')} in` : null
    const weight = unitWeight(unit)
    const rest = [size, weight > 0 ? `${weight.toLocaleString()} lb` : null].filter(Boolean).join(' · ')
    const key = `${unit.unit_type.toLowerCase()}|${rest}`
    const entry = counts.get(key) ?? { type: unit.unit_type.toLowerCase(), count: 0 }
    entry.count += unit.num_units
    counts.set(key, entry)
  }
  return [...counts].map(([key, { type, count }]) => {
    const rest = key.slice(key.indexOf('|') + 1)
    return [`${count} × ${type}`, rest].filter(Boolean).join(' · ')
  })
}
